import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAffiliateProducts() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['affiliateProducts'],
        queryFn: () => apiFetch('/affiliates/products', getAuthHeader()),
        staleTime: 60 * 1000,
    });
}

export function useAffiliateReferrals(filters = {}) {
    const { getAuthHeader } = useAuth();
    const params = new URLSearchParams();
    if (filters.product_id) params.set('product_id', filters.product_id);
    if (filters.status) params.set('status', filters.status);
    if (filters.page) params.set('page', filters.page);
    if (filters.page_size) params.set('page_size', filters.page_size);
    const qs = params.toString();
    return useQuery({
        queryKey: ['affiliateReferrals', qs],
        queryFn: async () => {
            const data = await apiFetch(`/affiliates/referrals${qs ? `?${qs}` : ''}`, getAuthHeader());
            return data;
        },
        staleTime: 30 * 1000,
    });
}

export function useAffiliateDashboard() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['affiliateDashboard'],
        queryFn: () => apiFetch('/affiliates/dashboard', getAuthHeader()),
        staleTime: 60 * 1000,
    });
}

export function useMyAffiliateLinks() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['myAffiliateLinks'],
        queryFn: () => apiFetch('/affiliates/my-links', getAuthHeader()),
        staleTime: 60 * 1000,
    });
}

export function useCreateProduct() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const res = await axios.post(`${API}/affiliates/products`, data, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['affiliateProducts'] });
            qc.invalidateQueries({ queryKey: ['affiliateDashboard'] });
        },
    });
}

export function useUpdateProduct() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ productId, data }) => {
            const res = await axios.put(`${API}/affiliates/products/${productId}`, data, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['affiliateProducts'] });
        },
    });
}

export function useDeleteProduct() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (productId) => {
            const res = await axios.delete(`${API}/affiliates/products/${productId}`, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['affiliateProducts'] });
            qc.invalidateQueries({ queryKey: ['affiliateDashboard'] });
        },
    });
}

export function useSyncProduct() {
    const { getAuthHeader } = useAuth();
    return useMutation({
        mutationFn: async (productId) => {
            const res = await axios.post(`${API}/affiliates/sync/${productId}`, {}, { headers: getAuthHeader() });
            return res.data;
        },
    });
}

export function useUpdateMyLinks() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (codes) => {
            const res = await axios.put(`${API}/affiliates/my-links`, { codes }, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['myAffiliateLinks'] });
        },
    });
}
